import * as React from "react";
import { GrozdLogo } from "./GrozdLogo";
import { GrapeIllustration } from "./GrapeIllustration";

export interface NotFoundPageProps {
  /** URL "Idi na početnu" dugmeta. Default "/". */
  homeUrl?: string;
  /** URL "Kontakt" dugmeta. Default "#". */
  contactUrl?: string;
  /** Footer link — Uslovi. */
  termsUrl?: string;
  /** Footer link — Privatnost. */
  privacyUrl?: string;
  /** Footer link — Kontakt. */
  footerContactUrl?: string;
}

/**
 * 404 — "Zrno nije u grozdu" stranica.
 *
 * 1:1 port iz mockupa: docs/Dizajn-Disciplina/mockups/404-i-greske.html (Stanje 1).
 *
 * Layout:
 * - Tamni gradient canvas (primary→deep) preko cijelog ekrana
 * - Bijela kartica (max 640px) u centru sa ilustracijom palog grozda
 * - Header sa Grozd logo lockup-om
 * - Footer linkovi u desnom donjem uglu (Uslovi · Privatnost · Kontakt)
 *
 * Dark mode: automatski kroz `[data-theme="dark"]` CSS vars (gradient se zatamni,
 * SVG ilustracija postaje bijela).
 */
export const NotFoundPage: React.FC<NotFoundPageProps> = ({
  homeUrl = "/",
  contactUrl = "#",
  termsUrl = "#",
  privacyUrl = "#",
  footerContactUrl = "#",
}) => {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(135deg, var(--primary) 0%, var(--deep) 100%)",
        padding: 24,
      }}
    >
      {/* Mesh overlay */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 15% 20%, var(--brand-mesh-1, rgba(197,183,238,.22)), transparent 50%), radial-gradient(circle at 85% 85%, var(--brand-mesh-2, rgba(155,134,223,.15)), transparent 55%)",
          pointerEvents: "none",
        }}
      />
      {/* Ghost ilustracija u uglu */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          right: -192,
          bottom: -192,
          opacity: 0.04,
          pointerEvents: "none",
        }}
      >
        <svg viewBox="0 0 200 240" width="720" height="720">
          <g
            transform="rotate(30,100,140)"
            fill="none"
            stroke="white"
            strokeWidth="5"
            strokeLinejoin="round"
            strokeLinecap="round"
          >
            <path d="M100 68Q114 48 132 40Q124 58 114 66Z" />
            <line x1="100" y1="68" x2="118" y2="52" strokeWidth="3" />
            <circle cx="60" cy="100" r="22" />
            <circle cx="100" cy="100" r="22" />
            <circle cx="140" cy="100" r="22" />
            <circle cx="80" cy="140" r="22" />
            <circle cx="120" cy="140" r="22" />
            <circle cx="100" cy="180" r="22" />
          </g>
        </svg>
      </div>

      {/* Header sa logom — PRAVILO 21: constrained na max-width kartice (640px),
          centriran u viewport-u, logo poravnat sa lijevom ivicom kartice. */}
      <div
        style={{
          width: "100%",
          maxWidth: 640,
          margin: "0 auto",
          padding: "32px 24px 16px",
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <a
          href={homeUrl}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            textDecoration: "none",
          }}
        >
          <GrozdLogo size={33} color="#fff" />
          <span
            style={{
              fontFamily: "var(--font-brand, 'Manrope'), system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 20,
              color: "#fff",
              letterSpacing: "-0.02em",
            }}
          >
            Grozd
          </span>
        </a>
      </div>

      {/* Body — kartica + footer */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 16px 40px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            margin: "0 auto",
            maxWidth: 640,
            width: "100%",
            background: "var(--surface-card, var(--white))",
            borderRadius: 16,
            border: "1px solid var(--n100)",
            boxShadow: "var(--shadow-card, 0 8px 32px rgba(90,58,181,.18))",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 24,
              padding: "40px 40px 48px",
              textAlign: "center",
            }}
          >
            <GrapeIllustration
              size={150}
              tilted
              color="var(--primary)"
              className="grozd-grape-illust"
            />

            <div style={{ marginTop: 48, maxWidth: 420 }}>
              <div
                style={{
                  fontFamily:
                    "var(--font-brand, 'Manrope'), system-ui, sans-serif",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.18em",
                  color: "var(--primary)",
                }}
              >
                Greška 404
              </div>
              <h1
                style={{
                  fontFamily:
                    "var(--font-brand, 'Manrope'), system-ui, sans-serif",
                  fontWeight: 800,
                  fontSize: 34,
                  letterSpacing: "-0.02em",
                  color: "var(--ink)",
                  lineHeight: 1.15,
                  marginTop: 8,
                }}
              >
                Zrno nije u grozdu.
              </h1>
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.6,
                  color: "var(--n600)",
                  maxWidth: 420,
                  marginTop: 8,
                }}
              >
                Izgleda da je zrno palo. Stranica koju tražiš ne postoji — možda
                je link star, možda je greška u kucanju. Vrati se na glavni
                grozd.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 8,
                width: "100%",
                maxWidth: 400,
                flexWrap: "wrap",
              }}
            >
              <a
                href={homeUrl}
                style={{
                  flex: 1,
                  minWidth: 160,
                  height: 48,
                  borderRadius: 10,
                  fontFamily:
                    "var(--font-brand, 'Manrope'), system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  background: "var(--primary)",
                  color: "#fff",
                  boxShadow: "0 4px 12px rgba(90,58,181,.25)",
                  textDecoration: "none",
                  transition: "all 150ms cubic-bezier(.22,1,.36,1)",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  width={16}
                  height={16}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                Idi na početnu
              </a>
              <a
                href={contactUrl}
                style={{
                  flex: 1,
                  minWidth: 160,
                  height: 48,
                  borderRadius: 10,
                  fontFamily:
                    "var(--font-brand, 'Manrope'), system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  background: "var(--surface-card, var(--white))",
                  color: "var(--primary)",
                  border: "1.5px solid var(--primary)",
                  textDecoration: "none",
                  transition: "all 150ms cubic-bezier(.22,1,.36,1)",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  width={16}
                  height={16}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Kontakt
              </a>
            </div>
          </div>
        </div>

        {/* Footer links */}
        <div
          style={{
            margin: "16px auto 0",
            maxWidth: 640,
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 4px",
          }}
        >
          <nav
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: "var(--p300)",
            }}
          >
            <a
              href={termsUrl}
              style={{ color: "var(--p300)", textDecoration: "none" }}
            >
              Uslovi
            </a>
            <span aria-hidden>·</span>
            <a
              href={privacyUrl}
              style={{ color: "var(--p300)", textDecoration: "none" }}
            >
              Privatnost
            </a>
            <span aria-hidden>·</span>
            <a
              href={footerContactUrl}
              style={{ color: "var(--p300)", textDecoration: "none" }}
            >
              Kontakt
            </a>
          </nav>
        </div>
      </div>
    </div>
  );
};
